import AddIcon from "@mui/icons-material/Add";
import { Button, Checkbox, Grid } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { cloneDeep, isArray } from "lodash";
import { memo, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import AsyncSelect, {
  AsyncSelectValue,
} from "../../../../../../../components/AsyncSelect";
// Specialty filter removed from ROS import — CustomSelect no longer needed
// import CustomSelect from "../../../../../../../components/CustomSelect";
import CustomTextArea from "../../../../../../../components/CustomTextArea";
import { FlexCenterWrapper } from "../../../../../../../components/DefaultNoDataWrapper";
import Flex from "../../../../../../../components/Flex";
import Text from "../../../../../../../components/Text";
import StyledFormModal from "../../../../../../../components/globalModal/StyledModal";
import StyledButtonV2 from "../../../../../../../components/app/StyledButtonV2";
import { useDebounce } from "../../../../../../../hooks/use-debounce";
import useDisclosure, {
  DisclosureHost,
} from "../../../../../../../hooks/use-disclosure";
import { useQueryParams } from "../../../../../../../hooks/use-query-param";
import { useSnackbar } from "../../../../../../../hooks/useSnackbar";
import { useRosPeTemplateCreate } from "../../../../../../../queries/encounter/use-ros-pe-template-create";
import { useRosPeTemplateUpdate } from "../../../../../../../queries/encounter/use-ros-pe-template-update";
import { useRosTemplateById } from "../../../../../../../queries/template/new-flow/use-new-template-by-id-list";
import { useNewTemplateList } from "../../../../../../../queries/template/new-flow/use-new-template-list";
// Specialty filter removed from ROS import
// import { useSpecialtiesList } from "../../../../../../../queries/use-specialities-list";
import {
  DEF_PAGINATION,
  getErrorSnackbar,
  getSuccessSnackbar,
  getXtenantId,
} from "../../../../../../../utils/utils";
import RosTemplateDrawer from "../../../../../admin/library/templates/RosTemplateDrawer";
import { newRosTemplateInitialData } from "../../../../../admin/library/templates/template-constant";
import { RosAndPe } from "../encounter-constant";
interface RosImportDrawerProps {
  disclosureHost: DisclosureHost;
}

const RosImportDrawer = memo(({ disclosureHost }: RosImportDrawerProps) => {
  const { mutateAsync: createRosTemplate } = useRosPeTemplateCreate();
  const { mutateAsync: updateRosTemplate } = useRosPeTemplateUpdate();
  const appointmentId = useQueryParams("appointmentid");
  const { patientId: routePatientId } = useParams();
  const queryPatientId = useQueryParams("patientid");
  const patientId = queryPatientId || routePatientId;

  const [ros, setRos] = useState<RosAndPe[] | null>(null);
  const [selectedTemplate, setSelectedTemplate] =
    useState<AsyncSelectValue | null>();
  // Specialty filter removed from ROS import
  // const [speciality, setSpeciality] = useState<string | null>();
  const [searchKey, setSearchKey] = useDebounce<string>("");
  // const [specialitySearch, setSpecialitySearch] = useDebounce<string>("");
  const showSnackbar = useSnackbar();
  const rosTemplateDisc = useDisclosure();
  const queryClient = useQueryClient();

  // const { data: specialities } = useSpecialtiesList({
  //   searchString: specialitySearch,
  //   xTENANTID: getXtenantId(),
  // });

  // const specialtiesList = useMemo(
  //   () =>
  //     specialities?.data?.content?.map((it) => ({
  //       value: it?.uuid,
  //       label: it?.name,
  //     })),
  //   [specialities]
  // );

  const { data, isLoading, isRefetching } = useNewTemplateList({
    enabled: disclosureHost.opened,
    pagination: DEF_PAGINATION,
    gridSorting: [],
    searchKey,
    templateType: "ROS",
    // specialityUuids: speciality ? [speciality] : undefined,
    xTENANTID: getXtenantId(),
  });

  const rosTemplateList = useMemo(() => {
    return data?.data?.content?.map((it) => ({
      value: it.uuid,
      label: it?.templateName,
    }));
  }, [data]);

  const { data: templateByIdData } = useRosTemplateById({
    rosTemplateUuid: selectedTemplate?.value,
    xTENANTID: getXtenantId(),
  });

  useEffect(() => {
    if (selectedTemplate?.value) {
      setRos(
        templateByIdData?.data?.rosTemplateTypeSet?.map((it: any) => ({
          templateType: "ROS",
          templateNameType: it?.name,
          description: it?.value,
          isSelected: it?.isSelected,
        })) ?? null
      );
    } else if (disclosureHost?.data && isArray(disclosureHost.data)) {
      setRos(disclosureHost?.data?.map((it) => ({ ...it, isSelected: true })));
    } else if (disclosureHost?.data) {
      setRos([{ ...disclosureHost?.data, isSelected: true }]);
    }
  }, [disclosureHost.opened, templateByIdData]);

  const handleSubmit = () => {
    const payload = {
      physicalROSTemplateRequest: {
        appointmentId: appointmentId ?? undefined,
        patientId: patientId ?? undefined,
        physicalROSTemplateList: ros?.filter((it) => !!it.isSelected),
      },
    };

    (disclosureHost?.data || !isArray(disclosureHost?.data)
      ? updateRosTemplate(payload)
      : createRosTemplate(payload)
    )
      .then((res) => {
        showSnackbar(getSuccessSnackbar(res));
        queryClient.invalidateQueries({
          queryKey: ["patient-ros-pe-template-list"],
        });
        closeAndReset();
      })
      .catch((err) => {
        showSnackbar(getErrorSnackbar(err));
      });
  };

  const handleSelect = (ind: number) => {
    const copiedRos = cloneDeep(ros);
    if (copiedRos) {
      copiedRos[ind].isSelected = !copiedRos[ind].isSelected;
      setRos(copiedRos);
    }
  };

  const onDescriptionChange = (v: string, ind: number) => {
    const copiedRos = cloneDeep(ros);
    if (copiedRos) {
      copiedRos[ind].description = v;
      setRos(copiedRos);
    }
  };

  const closeAndReset = () => {
    setRos(null);
    setSelectedTemplate(null);
    // setSpeciality(null);
    disclosureHost.close();
  };

  return (
    <StyledFormModal
      open={disclosureHost.opened}
      onClose={closeAndReset}
      dialogTitle="Add ROS"
      dialogAction={
        <Flex gap={1}>
          <StyledButtonV2
            label="Save"
            variant="contained"
            onClick={handleSubmit}
          />
          <StyledButtonV2
            label="Cancel"
            variant="outlined"
            onClick={closeAndReset}
          />
        </Flex>
      }
      sx={{
        "& .MuiDialog-paper": {
          width: "50vw",
          height: "65vh",
        },
      }}
    >
      <Flex flexDirection={"column"} gap={1} width={"100%"} pt={2}>
        {(isArray(disclosureHost.data) || !disclosureHost?.data) && (
          <Grid container spacing={1}>
            {/* Specialty filter removed from ROS import */}
            {/* <Grid item xs={3}>
              <CustomSelect
                label="Speciality"
                placeholder="Speciality"
                options={specialtiesList}
                onSearch={(_, val) => setSpecialitySearch(val)}
                value={speciality}
                onChange={(v) => setSpeciality(v)}
              />
            </Grid> */}
            <Grid size={10}>
              <AsyncSelect
                loading={isLoading || isRefetching}
                placeholder="Select Template"
                value={selectedTemplate}
                options={rosTemplateList}
                onSearch={setSearchKey}
                onChange={(v) => setSelectedTemplate(v)}
              />
            </Grid>
            <Grid size={1}>
              <Button
                sx={{ marginLeft: 0.5, marginTop: 1 }}
                variant="contained"
                onClick={() =>
                  rosTemplateDisc.open({
                    templateTypes: "ROS",
                    rosTemplateTypeSet: newRosTemplateInitialData,
                  })
                }
              >
                <AddIcon />
              </Button>
            </Grid>
            <RosTemplateDrawer disclosureHost={rosTemplateDisc} />
          </Grid>
        )}

        {((!ros?.length && !disclosureHost?.data) ||
          (isArray(disclosureHost?.data) &&
            !disclosureHost?.data?.length &&
            !ros?.length)) && (
          <Flex height={"60vh"}>
            <FlexCenterWrapper>{"Please Select Template"}</FlexCenterWrapper>
          </Flex>
        )}

        {ros?.map((it, ind) => (
          <Flex key={ind} gap={2} alignItems={"start"}>
            <Checkbox
              checked={it.isSelected}
              onChange={() => handleSelect(ind)}
            />
            <Flex flexDirection={"column"} width={"100%"} mt={1.5}>
              <Text>{it?.templateNameType}</Text>
              {it.isSelected && (
                <CustomTextArea
                  id="outlined-multiline-static"
                  variant="outlined"
                  name="desc"
                  placeholder="Enter Description"
                  value={it?.description}
                  onChange={(e) => {
                    onDescriptionChange(e.target.value, ind);
                  }}
                />
              )}
            </Flex>
          </Flex>
        ))}
      </Flex>
    </StyledFormModal>
  );
});

export default RosImportDrawer;
